import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Bell, Send, Loader2, AlertTriangle, Copy, RefreshCw, CheckCircle2 } from "lucide-react";

import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRemindersDue() {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [suggestedCount, setSuggestedCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payments/admin/reminders-due`);
      const list = res.data?.reminders || [];
      setRows(list);
      setSuggestedCount(res.data?.suggested_count || 0);
      // Opt-in review: start with NOTHING selected so a stray click can never
      // mass-email real parents. Admin ticks rows deliberately.
      setSelected({});
    } catch (e) {
      toast.error("Failed to load reminders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (selectedIds.length === rows.length) setSelected({});
    else { const all = {}; rows.forEach((r) => (all[r.id] = true)); setSelected(all); }
  };
  const selectSuggested = () => {
    const s = {};
    rows.forEach((r) => { if (r.suggested) s[r.id] = true; });
    setSelected(s);
  };

  const send = async () => {
    if (selectedIds.length === 0) { toast.error("Select at least one invoice"); return; }
    if (!window.confirm(`Send ${selectedIds.length} reminder email(s)?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/payments/admin/send-reminders`, { transaction_ids: selectedIds });
      const { sent_count, skipped_count, error_count } = res.data || {};
      toast.success(`Sent ${sent_count} reminder(s)` + (skipped_count ? `, skipped ${skipped_count}` : "") + (error_count ? `, ${error_count} error(s)` : ""));
      await load();
    } catch (e) {
      toast.error("Failed to send reminders");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6" data-testid="reminders-due-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4" data-testid="reminders-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Bell className="w-7 h-7 text-emerald-400" /> Reminders Due
            </h1>
            <p className="text-slate-400 mt-1 text-sm">
              Review overdue unpaid invoices, then send friendly reminders. Nothing is emailed automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={selectSuggested} disabled={loading || suggestedCount === 0}
              data-testid="reminders-select-suggested-btn" className="border-emerald-700 text-emerald-300">
              Select suggested ({suggestedCount})
            </Button>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="reminders-refresh-btn"
              className="border-slate-700 text-slate-300">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={send} disabled={sending || selectedIds.length === 0} data-testid="reminders-send-btn"
              className="bg-emerald-600 hover:bg-emerald-500">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
            </Button>
          </div>
        </div>

        <div className="flex gap-3 mb-4 text-sm text-slate-400">
          <span data-testid="reminders-total-count">{rows.length} overdue</span>
          <span>•</span>
          <span>{suggestedCount} suggested</span>
          <span>•</span>
          <span>{selectedIds.length} selected</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <Card className="p-12 bg-slate-900 border-slate-800 text-center" data-testid="reminders-empty">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="text-slate-300">No overdue invoices need reminders right now. 🎉</p>
          </Card>
        ) : (
          <Card className="bg-slate-900 border-slate-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/60 text-slate-400">
                <tr>
                  <th className="p-3 text-left w-10">
                    <Checkbox checked={selectedIds.length === rows.length && rows.length > 0}
                      onCheckedChange={toggleAll} data-testid="reminders-select-all" />
                  </th>
                  <th className="p-3 text-left">Parent</th>
                  <th className="p-3 text-left">Reference</th>
                  <th className="p-3 text-right">Amount</th>
                  <th className="p-3 text-right">Overdue</th>
                  <th className="p-3 text-center">Sent</th>
                  <th className="p-3 text-left">Flags</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-800/30"
                    data-testid={`reminder-row-${r.id}`}>
                    <td className="p-3">
                      <Checkbox checked={!!selected[r.id]} onCheckedChange={() => toggle(r.id)}
                        data-testid={`reminder-check-${r.id}`} />
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-white">{r.parent_name || "—"}</div>
                      <div className="text-xs text-slate-500">{r.parent_email || "no email"}</div>
                    </td>
                    <td className="p-3 font-mono text-xs text-slate-300">
                      <span className="inline-flex items-center gap-1">
                        {r.payment_reference}
                        <Copy className="w-3 h-3 cursor-pointer text-slate-400 hover:text-white"
                          onClick={() => { navigator.clipboard.writeText(r.payment_reference); toast.success("Copied"); }} />
                      </span>
                    </td>
                    <td className="p-3 text-right font-semibold">${Number(r.amount).toFixed(2)}</td>
                    <td className="p-3 text-right text-slate-300">{r.days_overdue}d</td>
                    <td className="p-3 text-center text-slate-300">{r.reminder_count || 0}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {r.possibly_paid && (
                          <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/40" data-testid={`flag-paid-${r.id}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" /> Possibly paid
                          </Badge>
                        )}
                        {r.possible_duplicate && (
                          <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/40" data-testid={`flag-dup-${r.id}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" /> Possible duplicate
                          </Badge>
                        )}
                        {r.suggested && !r.possibly_paid && !r.possible_duplicate && (
                          <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">Suggested</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        <p className="text-xs text-slate-500 mt-4">
          Nothing is selected by default — tick the invoices you want to remind (or use “Select suggested”), then Send.
          Rows flagged <span className="text-amber-300">Possibly paid</span> or <span className="text-orange-300">Possible duplicate</span> are never counted as “suggested” — verify those before sending.
        </p>
      </div>
    </div>
  );
}
